import { Category } from '@shared/types/product/Category';

/**
 * EWM-837 (strand A1) — project a commercetools Category down to the fields
 * that are actually read on the CLIENT.
 *
 * Every page serialises the whole category tree into its RSC flight payload,
 * because `app/[locale]/layout.tsx` seeds the flat + tree lists into
 * `<Providers>` (a Client Component) as SWR fallback data. Measured on
 * production 2026-08-03 that was ~710 bytes × ~1,700 nodes — 95% of a PDP's
 * weight and 89% of a category page's, to render a nav.
 *
 * The fix is NOT to fetch less: the server still reads the FULL category via
 * `readFlatCategories()` / `readTreeCategories()`, so everything resolved
 * server-side keeps every field — SEO metadata (`generateMetadata` →
 * `seo-tools`), the sitemaps, and the PLP's own `resolvedCategory` (which is
 * passed to the client whole, as a single node). Only the ~1,700-node list
 * crossing the client boundary is trimmed.
 *
 * ## What the client actually reads, and where
 *
 * | Field                     | Consumer                                              |
 * | ------------------------- | ----------------------------------------------------- |
 * | `categoryId`              | `mapCategory`; EWM-595 facet ancestor match           |
 * | `slug`                    | `mapCategory` → `categoryKey`                          |
 * | `name`                    | nav labels; EWM-595 facet names                        |
 * | `description`             | `mapCategory` → `EntityCategory.description`           |
 * | `_url`                    | `mapCategory` paths; the locale switcher needs EVERY   |
 * |                           | locale (`ship-and-language` matches across values)     |
 * | `descendants`             | nav slide-out recursion; intermediary-page count       |
 * | `ancestors[].categoryId`  | EWM-595 immediate-child facet rule                     |
 * | `ancestors[].name`        | EWM-539 breadcrumb trail                               |
 *
 * ## What is dropped, and why it is safe
 *
 * - `seoContent` (rich-text HTML) and `categoryFilter` (JSON array) are the
 *   heavy ones. Both are needed for ONE node per page — the current category —
 *   which reaches the client as `resolvedCategory`, untouched. The exception is
 *   `/search`, which scans every node's `categoryFilter`; that route asks for
 *   {@link toSearchFacetCategories} instead.
 * - `metaTitle` / `metaDescription` / `metaKeywords` are read only by
 *   `seo-tools`, which runs in `generateMetadata` — server-side, full data.
 * - `depth` is read only by the PLP tastic, which is an async Server Component.
 * - `parentId` / `parentKey` / `parentRef` / `categoryRef` have zero consumers
 *   anywhere in the frontend.
 *
 * ⚠ The ticket's own A1 sketch proposed dropping `ancestors`, `depth`,
 * `categoryId` and `metaTitle`. Verified against the code, `ancestors` +
 * `categoryId` are load-bearing on the client (the EWM-595 facet rule), so
 * they are KEPT here. `depth` and `metaTitle` are dropped only because their
 * readers turned out to be server-side.
 */

/** The ancestor chain is only ever read for these two fields. */
const toLeanAncestor = (ancestor: Category): Category => ({
  categoryId: ancestor.categoryId,
  name: ancestor.name,
});

const project = (category: Category, keepCategoryFilter: boolean): Category => ({
  categoryId: category.categoryId,
  categoryKey: category.categoryKey,
  name: category.name,
  slug: category.slug,
  description: category.description,
  _url: category._url,
  ...(category.ancestors ? { ancestors: category.ancestors.map(toLeanAncestor) } : {}),
  ...(category.descendants ? { descendants: category.descendants.map((c) => project(c, keepCategoryFilter)) } : {}),
  ...(keepCategoryFilter && category.categoryFilter ? { categoryFilter: category.categoryFilter } : {}),
});

/**
 * The projection seeded into `<Providers>` and returned by `useCategories`.
 *
 * Both MUST use this same function: SWR revalidates the seeded key on mount,
 * so a fuller client refetch would hand components a different shape than the
 * one they first rendered with.
 */
export const toLeanCategories = (categories: Category[]): Category[] =>
  categories.map((category) => project(category, false));

/**
 * `/search` variant — same trim, but `categoryFilter` is retained.
 *
 * The search route has no single current category, so its filter drawer is
 * built by scanning EVERY category's `categoryFilter` for ones matching the
 * result set (`product-list-view-model`). Category routes read the current
 * category's `categoryFilter` off `resolvedCategory` instead and don't need it
 * per-node — which is why the 1.9 MB category page, the worst case measured,
 * still gets the full trim.
 */
export const toSearchFacetCategories = (categories: Category[]): Category[] =>
  categories.map((category) => project(category, true));

/**
 * `toLeanCategories` over a `PaginatedResult`-shaped payload, which is what the
 * BFF returns and what the SWR fallback is keyed on. Keeps the envelope
 * (`total`, `count`, cursors) so the seeded value stays shape-compatible with a
 * live `queryCategories` response.
 */
export const toLeanCategoryResult = <T extends { items?: Category[] }>(result: T): T => ({
  ...result,
  items: toLeanCategories(result.items ?? []),
});
